import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import '../styles/Games.css';
import InventaireRoue from '../components/InventaireRoue';

// ── Couleurs par rareté ───────────────────────────────────────────────────────
const RARITY_COLORS = {
  'Commun':     '#B0B0B0',
  'Peu commun': '#A0D0A0',
  'Rare':       '#9C7AFF',
  'Épique':     '#FFD700',
  'Légendaire': '#FF6B6B',
  'Aucun':      '#444',
};

const RARITY_WEIGHT = {
  'Légendaire': 5,
  'Épique': 4,
  'Rare': 3,
  'Peu commun': 2,
  'Commun': 1,
  'Aucun': 0,
};

const CANONICAL_WHEEL_REWARD_NAMES = {
  1: 'Panoplie d\'Eliacube',
  2: 'Panoplie Bouftou en Chocolat',
  3: 'Panoplie de Givre',
  4: 'Panoplie du Dragon Noir',
  12: 'Panoplie du Casino',
};

function getCanonicalWheelRewardName(itemId, rawName) {
  const canonical = CANONICAL_WHEEL_REWARD_NAMES[Number(itemId)];
  if (canonical) return canonical;
  return String(rawName || '').trim();
}

function getDisplayWheelLabel(slot) {
  const itemId = Number(slot?.itemId ?? slot?.item_id);
  if (itemId === 12) return 'Casino';
  if (itemId === 4) return 'Dragon Noir';
  if (itemId === 3) return 'Givre';

  const name = String(slot?.name || '').trim();
  const label = String(slot?.label || '').trim();
  if (/dragon\s*noir/i.test(name)) return 'Dragon Noir';
  if (/pararayon|casino/i.test(name) || /pararayon/i.test(label)) return 'Casino';

  return label || getCanonicalWheelRewardName(itemId, slot?.name);
}

const SIZE   = 400;
const CX     = SIZE / 2;
const CY     = SIZE / 2;
const RADIUS = 172;

// ── Préchargement des icônes pour le canvas ────────────────────────────────────────────
const _wheelTitreImg = new Image(); _wheelTitreImg.src = '/assets/images/titre.png';

const CASINO_ICON_PATH = `${process.env.PUBLIC_URL}/assets/Items/Casino/86150.png`;
const ELIACUBE_ICON_PATH = `${process.env.PUBLIC_URL}/assets/Items/Eliacube/208.png`;
const CHOCOLAT_ICON_PATH = `${process.env.PUBLIC_URL}/assets/Items/Chocolat/86617.png`;
const GIVRE_ICON_PATH = '/assets/Items/Givre/Capuche de resident du givre.webp';
const DRAGON_ICON_PATH = `${process.env.PUBLIC_URL}/assets/Items/Dragon/86013.png`;

const ICON_PATHS = {
  1: ELIACUBE_ICON_PATH,
  2: CHOCOLAT_ICON_PATH,
  3: GIVRE_ICON_PATH,
  4: DRAGON_ICON_PATH,
  12: CASINO_ICON_PATH,
  13: `${process.env.PUBLIC_URL}/assets/Items/id13.png`,
  14: `${process.env.PUBLIC_URL}/assets/Items/id14.png`,
  15: `${process.env.PUBLIC_URL}/assets/Items/id15.png`,
  16: `${process.env.PUBLIC_URL}/assets/Items/id16.png`,
  17: `${process.env.PUBLIC_URL}/assets/Items/id17.png`,
  18: `${process.env.PUBLIC_URL}/assets/Items/id18.png`,
  19: `${process.env.PUBLIC_URL}/assets/Items/id19.png`,
  20: `${process.env.PUBLIC_URL}/assets/Items/id20.png`,
  21: `${process.env.PUBLIC_URL}/assets/Items/id21.png`,
  22: `${process.env.PUBLIC_URL}/assets/Items/id22.png`,
  23: `${process.env.PUBLIC_URL}/assets/Items/id23.png`,
  24: `${process.env.PUBLIC_URL}/assets/Items/id19.png`,
  25: `${process.env.PUBLIC_URL}/assets/Items/id25.png`,
  26: `${process.env.PUBLIC_URL}/assets/Items/id26.png`,
  27: `${process.env.PUBLIC_URL}/assets/Items/id27.png`,
  28: `${process.env.PUBLIC_URL}/assets/Items/id28.png`,
  29: `${process.env.PUBLIC_URL}/assets/Items/id29.png`,
};

const _skinImgs = {};
Object.entries(ICON_PATHS).forEach(([id, path]) => {
  _skinImgs[id] = new Image();
  _skinImgs[id].src = path;
});

function getWheelIcon(slot) {
  if (slot.category === 'skin') return _skinImgs[slot.itemId] || null;
  if (slot.category === 'titre') return _wheelTitreImg;
  return null;
}

// ── Icônes rewards ─────────────────────────────────────────────────────────────────────────────
function JetonIcon({ size = 16 }) {
  return <img src="/assets/images/jeton.png" alt="jeton" style={{ width: size, height: size, verticalAlign: 'middle', objectFit: 'contain' }} />;
}
function TitreIcon({ size = 16 }) {
  return <img src="/assets/images/titre.png" alt="titre" style={{ width: size, height: size, verticalAlign: 'middle', objectFit: 'contain' }} />;
}
function OgIcon({ size = 16 }) {
  return <img src="/assets/images/1.png" alt="og" style={{ width: size, height: size, verticalAlign: 'middle' }} />;
}

// ── Utilitaires canvas ────────────────────────────────────────────────────────
function boltPoints(x1, y1, x2, y2, depth) {
  if (depth <= 0) return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
  const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * depth * 14;
  const my = (y1 + y2) / 2 + (Math.random() - 0.5) * depth * 14;
  const left  = boltPoints(x1, y1, mx, my, depth - 1);
  const right = boltPoints(mx, my, x2, y2, depth - 1);
  right.shift();
  return [...left, ...right];
}

function drawBoltPath(ctx, pts, alpha, color) {
  if (pts.length < 2) return;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.strokeStyle = color; ctx.lineWidth = 2.2;
  ctx.shadowColor = color; ctx.shadowBlur = 22;
  ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.88)'; ctx.lineWidth = 0.7; ctx.shadowBlur = 5;
  ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.stroke();
  ctx.restore();
}

function drawFrame(ctx, rotation, wheel) {
  if (!wheel || wheel.length === 0) return;
  const NUM_SEG   = wheel.length;
  const SEG_ANGLE = (2 * Math.PI) / NUM_SEG;

  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.beginPath(); ctx.arc(CX, CY, RADIUS + 8, 0, 2 * Math.PI);
  ctx.fillStyle = '#0d0121'; ctx.fill();

  wheel.forEach((slot, i) => {
    const a0 = rotation + i * SEG_ANGLE, a1 = a0 + SEG_ANGLE;
    
    ctx.beginPath(); ctx.moveTo(CX, CY); ctx.arc(CX, CY, RADIUS, a0, a1); ctx.closePath();
    ctx.fillStyle = slot.color; ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.lineWidth = 1.8; ctx.stroke();

    ctx.save();
    ctx.translate(CX, CY);
    ctx.rotate(a0 + SEG_ANGLE / 2);
    
    const icon = getWheelIcon(slot);
    
    if (icon && icon.complete && icon.naturalWidth) {
      const ICON = 34; 
      ctx.shadowBlur = 5;
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.drawImage(icon, RADIUS - 15 - ICON, -ICON / 2, ICON, ICON);
    } else {
      const displayLabel = getDisplayWheelLabel(slot);
      ctx.textAlign   = 'right';
      ctx.fillStyle   = slot.rarity === 'Aucun' ? '#666' : (RARITY_COLORS[slot.rarity] || '#ccc');
      ctx.font        = 'bold 13px Segoe UI,Arial,sans-serif';
      ctx.shadowColor = 'rgba(0,0,0,0.95)';
      ctx.shadowBlur  = 6;
      ctx.fillText(displayLabel, RADIUS - 15, 5);
    }
    
    ctx.restore();
  });

  ctx.beginPath(); ctx.arc(CX, CY, RADIUS, 0, 2 * Math.PI);
  ctx.strokeStyle = 'rgba(180,95,255,0.6)'; ctx.lineWidth = 4; ctx.stroke();

  for (let i = 0; i < NUM_SEG; i++) {
    const angle = rotation + i * SEG_ANGLE;
    ctx.save(); ctx.translate(CX, CY); ctx.rotate(angle);
    ctx.beginPath(); ctx.moveTo(RADIUS - 4, 0); ctx.lineTo(RADIUS + 7, 0);
    ctx.strokeStyle = 'rgba(180,95,255,0.7)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.restore();
  }

  const grd = ctx.createRadialGradient(CX - 5, CY - 5, 2, CX, CY, 22);
  grd.addColorStop(0, '#666'); grd.addColorStop(1, '#111');
  ctx.beginPath(); ctx.arc(CX, CY, 22, 0, 2 * Math.PI);
  ctx.fillStyle = grd; ctx.fill();
  ctx.strokeStyle = '#9C27B0'; ctx.lineWidth = 2.5; ctx.stroke();

  const tipY = CY - RADIUS + 4;
  ctx.beginPath(); ctx.moveTo(CX, tipY); ctx.lineTo(CX - 13, tipY - 26); ctx.lineTo(CX + 13, tipY - 26);
  ctx.closePath(); ctx.fillStyle = '#9C27B0'; ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = 1.5; ctx.stroke();
}

const CRACK_COLORS = ['#9C27B0', '#B45FFF', '#FFD700', '#E040FB', '#40C4FF'];

function playRareVFX(mainCanvas, overlayCanvas, finalRotation, winSlot, wheel, onComplete) {
  if (!mainCanvas || !overlayCanvas) { onComplete(); return; }
  const mc    = mainCanvas.getContext('2d');
  const oc    = overlayCanvas.getContext('2d');
  const t0    = performance.now();
  const TOTAL = 3600;
  const NUM_SEG   = wheel.length;
  const SEG_ANGLE = (2 * Math.PI) / NUM_SEG;

  const cracks = Array.from({ length: 10 }, () => {
    const a = Math.random() * Math.PI * 2;
    const r = 18 + Math.random() * RADIUS;
    return {
      pts:   boltPoints(CX, CY, CX + Math.cos(a) * r, CY + Math.sin(a) * r, 3),
      color: CRACK_COLORS[Math.floor(Math.random() * CRACK_COLORS.length)],
    };
  });

  const rarityColor = RARITY_COLORS[winSlot.rarity] || '#9C27B0';

  const particles = Array.from({ length: 40 }, (_, i) => {
    const a   = (i / 40) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    const spd = 2 + Math.random() * 7;
    return {
      x: CX, y: CY,
      vx: Math.cos(a) * spd, vy: Math.sin(a) * spd - 2,
      grav: 0.14 + Math.random() * 0.1,
      rot: Math.random() * Math.PI * 2, rotV: (Math.random() - 0.5) * 0.25,
      size: 8 + Math.random() * 12,
      delay: 400 + Math.random() * 400,
      color: CRACK_COLORS[Math.floor(Math.random() * CRACK_COLORS.length)],
    };
  });

  let rafId;
  const frame = (now) => {
    const el   = now - t0;
    const prog = Math.min(el / TOTAL, 1);
    mc.clearRect(0, 0, SIZE, SIZE);
    oc.clearRect(0, 0, SIZE, SIZE);

    if (prog < 0.22) {
      const p  = prog / 0.22;
      const sh = (Math.random() - 0.5) * p * 8;
      const sv = (Math.random() - 0.5) * p * 6;
      mc.save(); mc.translate(sh, sv);
      drawFrame(mc, finalRotation, wheel);
      mc.restore();
      for (const c of cracks) {
        const n = Math.max(2, Math.floor(c.pts.length * p));
        drawBoltPath(oc, c.pts.slice(0, n), p * 0.8, c.color);
      }
    } else if (prog < 0.58) {
      const p = (prog - 0.22) / 0.36;
      mc.fillStyle = `rgba(13,1,33,${p * 0.98})`; mc.fillRect(0, 0, SIZE, SIZE);
      const hr = p * RADIUS * 1.3;
      const hg = mc.createRadialGradient(CX, CY, 0, CX, CY, Math.max(1, hr));
      hg.addColorStop(0, `rgba(180,95,255,${(1 - p) * 0.9})`);
      hg.addColorStop(0.6, `rgba(80,0,180,${(1 - p) * 0.5})`);
      hg.addColorStop(1, 'rgba(0,0,0,0)');
      mc.beginPath(); mc.arc(CX, CY, Math.max(1, hr), 0, Math.PI * 2);
      mc.fillStyle = hg; mc.fill();
      wheel.forEach((slot, i) => {
        const a0   = finalRotation + i * SEG_ANGLE;
        const a1   = a0 + SEG_ANGLE;
        const midA = a0 + SEG_ANGLE / 2;
        const off  = p * 85 + p * p * 55;
        mc.save();
        mc.translate(Math.cos(midA) * off, Math.sin(midA) * off);
        mc.globalAlpha = 1 - p * 0.97;
        mc.beginPath(); mc.moveTo(CX, CY); mc.arc(CX, CY, RADIUS, a0, a1); mc.closePath();
        mc.fillStyle = slot.color; mc.fill();
        mc.strokeStyle = rarityColor; mc.lineWidth = 2; mc.stroke();
        mc.restore();
      });
      for (const c of cracks) drawBoltPath(oc, c.pts, (1 - p) * 0.7, c.color);
    } else {
      const p = (prog - 0.58) / 0.42;
      mc.fillStyle = '#0d0121'; mc.fillRect(0, 0, SIZE, SIZE);
      const hr = RADIUS * (0.6 + p * 0.5);
      const hg = mc.createRadialGradient(CX, CY, 0, CX, CY, Math.max(1, hr));
      hg.addColorStop(0, `rgba(180,95,255,${(1 - p) * 0.35})`);
      hg.addColorStop(1, 'rgba(0,0,0,0)');
      mc.beginPath(); mc.arc(CX, CY, Math.max(1, hr), 0, Math.PI * 2);
      mc.fillStyle = hg; mc.fill();
      if (p < 0.85) {
        oc.save();
        oc.globalAlpha = Math.min(1, (1 - p / 0.85) * 1.5);
        oc.fillStyle   = rarityColor;
        oc.font        = 'bold 18px Segoe UI,Arial,sans-serif';
        oc.textAlign   = 'center';
        oc.shadowColor = rarityColor; oc.shadowBlur = 20;
        oc.fillText(winSlot.name, CX, CY - 10);
        oc.font = '13px Segoe UI,Arial,sans-serif'; oc.fillStyle = '#fff'; oc.shadowBlur = 8;
        oc.fillText(winSlot.rarity, CX, CY + 14);
        oc.restore();
      }
    }

    if (el > 350) {
      const partEl = el - 350;
      for (const p of particles) {
        const t = (partEl - p.delay) / 1000;
        if (t < 0) continue;
        const px    = p.x + p.vx * t * 50;
        const py    = p.y + p.vy * t * 50 + 0.5 * p.grav * (t * 50) ** 2;
        const alpha = Math.max(0, 1 - t / ((TOTAL - 350) / 1000));
        if (alpha <= 0) continue;
        oc.save();
        oc.globalAlpha = alpha; oc.fillStyle = p.color;
        oc.shadowColor = p.color; oc.shadowBlur = 10;
        oc.translate(px, py); oc.rotate(p.rot + p.rotV * t * 50);
        const s = p.size / 2;
        oc.beginPath();
        oc.moveTo(0, -s); oc.lineTo(s * 0.3, -s * 0.3); oc.lineTo(s, 0); oc.lineTo(s * 0.3, s * 0.3);
        oc.lineTo(0, s); oc.lineTo(-s * 0.3, s * 0.3); oc.lineTo(-s, 0); oc.lineTo(-s * 0.3, -s * 0.3);
        oc.closePath(); oc.fill();
        oc.restore();
      }
    }

    if (prog < 1) { rafId = requestAnimationFrame(frame); }
    else {
      oc.clearRect(0, 0, SIZE, SIZE);
      drawFrame(mc, finalRotation, wheel);
      onComplete();
    }
  };
  rafId = requestAnimationFrame(frame);
}

// ─────────────────────────────────────────────────────────────────────────────
export default function TokenWheel() {
  const canvasRef      = useRef(null);
  const overlayRef     = useRef(null);
  const rotRef         = useRef(-Math.PI / 2);
  const rafRef         = useRef(null);
  const boltPoolRef    = useRef([]);
  const boltCleanupRef = useRef(null);

  const [rareVfx,   setRareVfx]   = useState(false);
  const [spinning,  setSpinning]  = useState(false);
  
  // Remplacé 'result' par 'results' pour gérer x1 et x5
  const [results,   setResults]   = useState([]);     
  
  const [jetons,    setJetons]    = useState(null);
  const [ogrines,   setOgrines]   = useState(null);
  const [wheel,     setWheel]     = useState([]);     
  const [myWins,    setMyWins]    = useState([]);     
  const [error,     setError]     = useState('');
  const [buyMsg,    setBuyMsg]    = useState('');
  const [buying,    setBuying]    = useState(false);

  // ── Modal de confirmation "En jeu" ──
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [pendingMultiplier, setPendingMultiplier] = useState(1);
  const [inventaireOuvert, setInventaireOuvert] = useState(false);
  const [nbInventaire, setNbInventaire] = useState(0);

  // ── Pagination des gains ──
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const sessionToken = localStorage.getItem('sessionToken');
  const isLoggedIn   = !!localStorage.getItem('user');

  // ── Initialisation canvas ─────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || wheel.length === 0) return;
    const SEG_ANGLE = (2 * Math.PI) / wheel.length;
    rotRef.current = -Math.PI / 2 - SEG_ANGLE / 2;
    drawFrame(canvas.getContext('2d'), rotRef.current, wheel);
    
    const redraw = () => { if (canvasRef.current) drawFrame(canvasRef.current.getContext('2d'), rotRef.current, wheel); };
    Object.values(_skinImgs).forEach(img => {
      if (!img.complete) img.onload = redraw;
    });
    const tid = setTimeout(redraw, 400);
    return () => clearTimeout(tid);
  }, [wheel]);

  // ── Chargement données joueur ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!isLoggedIn || !sessionToken) return;
    try {
      const [statusRes, winsRes] = await Promise.all([
        fetch(`${API_URL}/token-wheel/status`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        fetch(`${API_URL}/token-wheel/my-wins`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
      ]);
      const statusData = await statusRes.json();
      const winsData   = await winsRes.json();
      if (statusData.success) {
        setJetons(statusData.jetons);
        if (statusData.wheel) setWheel(statusData.wheel);
      }
      if (winsData.success) setMyWins(winsData.wins);
      const invRes = await fetch(`${API_URL}/token-wheel/inventory`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      const invData = await invRes.json().catch(() => ({}));
      if (invRes.ok && invData.success && Array.isArray(invData.inventory)) {
        setNbInventaire(invData.inventory.filter((l) => l.status === 'PENDING' || l.status === 'FAILED').length);
      }
    } catch (_) {}

    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    if (stored) setOgrines(stored.ogrines ?? 0);
  }, [isLoggedIn, sessionToken]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Animation spin ────────────────────────────────────────────────────
  const animateToSegment = useCallback((targetIdx, onDone) => {
    if (!wheel.length) { onDone(); return; }
    const NUM_SEG   = wheel.length;
    const SEG_ANGLE = (2 * Math.PI) / NUM_SEG;
    const INIT_ROT  = -Math.PI / 2 - SEG_ANGLE / 2;

    const numSpins = 5 + Math.floor(Math.random() * 3);
    let targetRot = INIT_ROT - targetIdx * SEG_ANGLE;
    while (targetRot < rotRef.current + numSpins * 2 * Math.PI) targetRot += 2 * Math.PI;

    const startRot  = rotRef.current;
    const totalDist = targetRot - startRot;
    const duration  = 4200;
    const startTime = performance.now();

    const canvas  = canvasRef.current;
    const overlay = overlayRef.current;
    const ctx     = canvas?.getContext('2d');
    const octx    = overlay?.getContext('2d');

    const boltInterval = setInterval(() => {
      const angle = Math.random() * Math.PI * 2;
      const r     = RADIUS * (0.4 + Math.random() * 0.6);
      boltPoolRef.current.push({
        pts:   boltPoints(CX, CY, CX + Math.cos(angle) * r, CY + Math.sin(angle) * r, 3),
        color: CRACK_COLORS[Math.floor(Math.random() * CRACK_COLORS.length)],
        alpha: 0.8, decay: 0.06 + Math.random() * 0.07,
      });
      if (boltPoolRef.current.length > 5) boltPoolRef.current.shift();
    }, 130);

    const animate = (now) => {
      const elapsed = now - startTime;
      const t       = Math.min(elapsed / duration, 1);
      const ease    = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const cur     = startRot + totalDist * ease;
      rotRef.current = cur;

      if (ctx) drawFrame(ctx, cur, wheel);
      if (octx) {
        octx.clearRect(0, 0, SIZE, SIZE);
        boltPoolRef.current = boltPoolRef.current.filter(b => {
          b.alpha -= b.decay;
          if (b.alpha <= 0) return false;
          drawBoltPath(octx, b.pts, b.alpha, b.color);
          return true;
        });
      }

      if (t < 1) { rafRef.current = requestAnimationFrame(animate); }
      else {
        clearInterval(boltInterval);
        boltCleanupRef.current = setTimeout(() => {
          if (octx) octx.clearRect(0, 0, SIZE, SIZE);
        }, 500);
        onDone();
      }
    };
    rafRef.current = requestAnimationFrame(animate);
  }, [wheel]);

  // ── Spin ──────────────────────────────────────────────────────────────
  const handleSpinClick = (multiplier) => {
    if (spinning || !isLoggedIn) return;
    
    // Vérification de la préférence locale
    const hideConfirm = localStorage.getItem('hideInGameConfirm') === 'true';
    
    if (hideConfirm) {
      executeSpin(multiplier);
    } else {
      setPendingMultiplier(multiplier);
      setShowConfirmModal(true);
    }
  };

  const confirmSpinModal = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideInGameConfirm', 'true');
    }
    setShowConfirmModal(false);
    executeSpin(pendingMultiplier);
  };

  const executeSpin = useCallback(async (multiplier) => {
    setSpinning(true); setResults([]); setError('');
    try {
      const res  = await fetch(`${API_URL}/token-wheel/spin`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ multiplier })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Erreur lors du lancer.');
        setSpinning(false);
        return;
      }

      const serverWheel = data.wheel || wheel;
      if (data.wheel) setWheel(data.wheel);

      // On trouve le meilleur lot parmi les tirages pour faire arrêter la roue dessus (Effet visuel)
      const bestResult = data.results.reduce((prev, current) => {
        return (RARITY_WEIGHT[current.slot.rarity] || 0) > (RARITY_WEIGHT[prev.slot.rarity] || 0) ? current : prev;
      }, data.results[0]);

      animateToSegment(bestResult.slotIndex, () => {
        const applyResults = () => {
          setResults(data.results);
          setJetons(data.newBalance);
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          stored.jetons_paradox = data.newBalance;
          localStorage.setItem('user', JSON.stringify(stored));
          window.dispatchEvent(new Event('storage'));
          setCurrentPage(1); 
          setSpinning(false);
          setRareVfx(false);
          loadData();
        };

        const isRare = bestResult.slot.rarity === 'Épique' || bestResult.slot.rarity === 'Légendaire';
        if (isRare && bestResult.slot.category !== 'aucun') {
          clearTimeout(boltCleanupRef.current);
          setRareVfx(true);
          playRareVFX(canvasRef.current, overlayRef.current, rotRef.current, bestResult.slot, serverWheel, applyResults);
        } else {
          applyResults();
        }
      });
    } catch (_) {
      setError('Impossible de contacter le serveur.');
      setSpinning(false);
    }
  }, [spinning, isLoggedIn, sessionToken, wheel, animateToSegment, loadData]);

  // ── Achat jeton ───────────────────────────────────────────────────────
  const buyToken = useCallback(async () => {
    if (buying || !isLoggedIn) return;
    setBuying(true); setBuyMsg(''); setError('');
    try {
      const res  = await fetch(`${API_URL}/token-wheel/buy-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Erreur lors de l\'achat.');
      } else {
        setJetons(data.newJetons);
        setOgrines(data.newOgrines);
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.ogrines        = data.newOgrines;
        stored.jetons_paradox = data.newJetons;
        localStorage.setItem('user', JSON.stringify(stored));
        window.dispatchEvent(new Event('storage'));
        if (data.ogrinesSpent !== undefined) {
          window.dispatchEvent(new CustomEvent('ogSpentUpdate', { detail: { ogrinesSpent: data.ogrinesSpent } }));
        }
        setBuyMsg('✅ Jeton acheté !');
        setTimeout(() => setBuyMsg(''), 3000);
      }
    } catch (_) {
      setError('Impossible de contacter le serveur.');
    }
    setBuying(false);
  }, [buying, isLoggedIn, sessionToken]);

  // ── Calculs pour la pagination ──
  const totalPages = Math.ceil(myWins.length / ITEMS_PER_PAGE);
  const currentWins = myWins.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div>
      <InventaireRoue
        ouvert={inventaireOuvert}
        onFermer={() => setInventaireOuvert(false)}
        sessionToken={sessionToken}
        onNombre={setNbInventaire}
        icones={ICON_PATHS}
        raretes={Object.fromEntries(wheel.map((s) => [s.itemId, s.rarity]))}
        couleurs={RARITY_COLORS}
      />
      {/* ── Modal de Confirmation En Jeu ── */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: '#1a1025', padding: '24px 30px', borderRadius: 12,
            border: '1px solid #4a3065', maxWidth: 400, textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ marginTop: 0, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              ⚠️ Attention
            </h3>
            <p style={{ color: '#ccc', fontSize: '0.95rem', marginBottom: 20, lineHeight: 1.5 }}>
              Vos gains sont rangés dans l'inventaire de la roue. Vous les transférez ensuite en jeu, quand votre personnage est connecté.
            </p>
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              color: '#aaa', fontSize: '0.85rem', cursor: 'pointer', marginBottom: 24
            }}>
              <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} />
              Ne plus m'afficher ce message
            </label>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button 
                onClick={() => setShowConfirmModal(false)} 
                style={{ padding: '10px 18px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontWeight: 600, transition: '0.2s' }}
                onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
                onMouseOut={e => e.target.style.background = 'rgba(255,255,255,0.1)'}
              >
                Annuler
              </button>
              <button 
                onClick={confirmSpinModal} 
                style={{ padding: '10px 18px', background: '#9C27B0', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontWeight: 600, transition: '0.2s', boxShadow: '0 0 10px rgba(156, 39, 176, 0.5)' }}
                onMouseOver={e => e.target.style.background = '#B45FFF'}
                onMouseOut={e => e.target.style.background = '#9C27B0'}
              >
                Confirmer le lancer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="gc-header" style={{ marginBottom: 12 }}>
        <div className="gc-title" style={{ flex: 1, justifyContent: 'center' }}>
          <span className="gc-icon">🎟️</span>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0 }}>Roulette Votes</h2>
            <span className="gc-sub">Jetons obtenus par votes · Skins &amp; titres exclusifs</span>
          </div>
        </div>
      </div>

      <div className="games-layout">
        {/* ── Roue ── */}
        <section className="game-card roulette-section">
          <div className={'wheel-wrapper' + (spinning ? ' is-spinning' : '') + (rareVfx ? ' jackpot-vfx' : '')}>
            <div className="wheel-ring-wrap tw-ring">
              <canvas ref={canvasRef}  width={SIZE} height={SIZE} className="wheel-canvas"  />
              <canvas ref={overlayRef} width={SIZE} height={SIZE} className="wheel-overlay" />
            </div>
          </div>

          {!isLoggedIn ? (
            <div className="auth-guard">Connectez-vous pour jouer à la roulette</div>
          ) : (
            <div className="spin-row" style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                className={'spin-btn' + (spinning ? ' is-spinning' : '')}
                onClick={() => handleSpinClick(1)}
                disabled={spinning || jetons < 1}
                style={{ flex: 1, maxWidth: 180 }}
              >
                {spinning ? 'En cours...' : <span>Lancer — 1 <JetonIcon size={14} /></span>}
              </button>
              
              <button
                className={'spin-btn' + (spinning ? ' is-spinning' : '')}
                onClick={() => handleSpinClick(5)}
                disabled={spinning || jetons < 4}
                style={{ flex: 1, maxWidth: 180, background: 'linear-gradient(135deg, #FF9800, #F44336)', border: 'none' }}
              >
                {spinning ? 'En cours...' : <span>Lancer x5 — 4 <JetonIcon size={14} /></span>}
              </button>
            </div>
          )}

          {isLoggedIn && (
            <button
              type="button"
              className="refill-btn"
              onClick={() => setInventaireOuvert(true)}
              style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.9rem', fontWeight: 700 }}
            >
              Inventaire de la roue
              <span style={{ minWidth: 22, padding: '1px 7px', borderRadius: 12, background: '#FFD700', color: '#1a1025', fontSize: '0.75rem', fontWeight: 800 }}>{nbInventaire}</span>
            </button>
          )}

          {error && <div className="result-banner lose" style={{ marginTop: 12 }}>⚠️ {error}</div>}

          {/* Affichage du ou des gains */}
          {results && results.length > 0 && (
            <div
              className={'result-banner'}
              style={{ 
                borderLeft: `4px solid ${RARITY_COLORS[results[0].slot.rarity] || '#9C27B0'}`, 
                marginTop: 12,
                background: 'rgba(25, 15, 35, 0.8)',
                padding: '12px 16px',
                borderRadius: '8px'
              }}
            >
              {results.length === 1 ? (
                // Affichage pour 1 seul tirage
                results[0].category === 'aucun' ? (
                  <>😔 Aucun gain cette fois…</>
                ) : (
                  <>
                    <span style={{ fontWeight: 700, color: RARITY_COLORS[results[0].slot.rarity] }}>
                      {results[0].slot.rarity === 'Légendaire' ? '🌟 LÉGENDAIRE ! ' : results[0].slot.rarity === 'Épique' ? '💎 ÉPIQUE ! ' : '✅ '}
                    </span>
                    Vous avez obtenu : <strong>{results[0].slot.name}</strong>
                  </>
                )
              ) : (
                // Affichage pour 5 tirages
                <div style={{ width: '100%', textAlign: 'left' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#fff', fontSize: '1rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 5 }}>
                    🎁 Vos 5 récompenses :
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {results.map((res, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex', alignItems: 'center', gap: 8, 
                        background: 'rgba(0,0,0,0.2)', padding: '6px 10px', borderRadius: 6,
                        borderLeft: `3px solid ${RARITY_COLORS[res.slot.rarity] || '#444'}`
                      }}>
                        {res.category === 'aucun' ? (
                          <span style={{ color: '#888' }}>😔 Aucun gain</span>
                        ) : (
                          <>
                            <span style={{ color: RARITY_COLORS[res.slot.rarity], fontSize: '0.85rem', fontWeight: 'bold', width: 85 }}>
                              {res.slot.rarity}
                            </span>
                            <strong style={{ color: '#fff', fontSize: '0.9rem' }}>{res.slot.name}</strong>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!spinning && results && results.some((r) => r.slot && r.slot.category !== 'aucun') && (
            <p className="proba-note" style={{ marginTop: 8, textAlign: 'center' }}>
              Rangé dans votre inventaire de la roue : transférez-le en jeu quand votre personnage est connecté.{' '}
              <button
                type="button"
                onClick={() => setInventaireOuvert(true)}
                style={{ background: 'none', border: 'none', color: '#ce93d8', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 }}
              >
                Ouvrir l'inventaire
              </button>
            </p>
          )}

          {isLoggedIn && jetons === 0 && !spinning && (
            <p className="proba-note" style={{ marginTop: 10, textAlign: 'center', color: '#888' }}>
              Votez (1 <JetonIcon size={12} /> tous les 10 votes) ou achetez un jeton.
            </p>
          )}
        </section>

        {/* ── Aside : mes gains + solde ── */}
        <aside className="stats-aside">
          <div className="stats-card">
            <h3>Mes gains</h3>
            {!isLoggedIn ? (
              <p className="proba-note" style={{ marginTop: 6 }}>Connectez-vous pour voir vos gains.</p>
            ) : myWins.length === 0 ? (
              <p className="proba-note" style={{ marginTop: 6 }}>Aucun gain pour l'instant. Lancez la roue !</p>
            ) : (
              <>
                <table className="proba-table" style={{ marginTop: 4, width: '100%' }}>
                  <thead>
                    <tr><th>Item</th><th>Cat.</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {currentWins.map((w, i) => (
                      <tr key={i}>
                        <td><strong>{getCanonicalWheelRewardName(w.item_id, w.item_name)}</strong></td>
                        <td style={{ color: '#C0A0D0', fontSize: 11, textAlign: 'center' }}>
                          {ICON_PATHS[w.item_id] 
                            ? <img src={ICON_PATHS[w.item_id]} alt="item" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                            : w.item_id === 10 ? '' 
                            : <TitreIcon size={15} />
                          }
                        </td>
                        <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                          {new Date(w.created_at).toLocaleDateString('fr-FR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* ── Contrôles de Pagination ── */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '0 5px' }}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', 
                        padding: '4px 10px', borderRadius: '6px', cursor: currentPage === 1 ? 'default' : 'pointer', 
                        opacity: currentPage === 1 ? 0.3 : 1, fontSize: '0.75rem', transition: 'all 0.2s'
                      }}
                    >
                      Précédent
                    </button>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                      Page {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', 
                        padding: '4px 10px', borderRadius: '6px', cursor: currentPage === totalPages ? 'default' : 'pointer', 
                        opacity: currentPage === totalPages ? 0.3 : 1, fontSize: '0.75rem', transition: 'all 0.2s'
                      }}
                    >
                      Suivant
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Solde jetons + achat */}
            {isLoggedIn && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Mes jetons</span>
                  <span style={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 5 }}><JetonIcon size={16} /> {jetons ?? '…'}</span>
                </div>
                <button
                  style={{
                    width: '100%', padding: '8px 12px',
                    background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)',
                    borderRadius: 8, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                  }}
                  onClick={buyToken}
                  disabled={buying || (ogrines !== null && ogrines < 25000)}
                >
                  {buying ? 'Achat en cours…' : <span>Acheter 1 jeton — 25000 <OgIcon size={13} /></span>}
                </button>
                {ogrines !== null && (
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', margin: '4px 0 0', textAlign: 'right' }}>
                    Solde : {ogrines} <OgIcon size={11} />
                  </p>
                )}
                {buyMsg && <p style={{ fontSize: '0.8rem', color: '#A0D0A0', margin: '6px 0 0' }}>{buyMsg}</p>}
              </div>
            )}
          </div>

          {/* ── Gains possibles ── */}
          <div className="stats-card" style={{ marginTop: 10 }}>
            <h3>Gains possibles</h3>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {[
                { name: 'Jeton JP', rarity: 'Légendaire', id: 28 },
                { name: 'Dofus Votes', rarity: 'Légendaire', id: 20 },
                { name: 'Relique Votes', rarity: 'Légendaire', id: 27 },
                { name: 'Familier Paradoxe', rarity: 'Épique', id: 25 },
                { name: 'Ressource de modularité intemporelle', rarity: 'Épique', id: 29 },
                { name: 'Bouclier Paradoxe', rarity: 'Épique', id: 26 },
                { name: 'Potion drop x2', rarity: 'Rare', id: 15 },
                { name: 'Potion XP X2', rarity: 'Rare', id: 14 },
                { name: 'Pack Potions securise x100', rarity: 'Rare', id: 21 },
                { name: 'Energie rare X100', rarity: 'Rare', id: 22 },
                { name: 'Runes d\'amelioration temporelle x 100', rarity: 'Rare', id: 23 },
                { name: 'Potion 1H DJ Ilimité', rarity: 'Rare', id: 24 },
                { name: 'Box de skins Chapeaux', rarity: 'Commun', id: 13 },
                { name: 'Box de skins Cape', rarity: 'Commun', id: 16 },
                { name: 'Box de skins Boucliers', rarity: 'Commun', id: 17 },
                { name: 'Box de skins Familiers', rarity: 'Commun', id: 18 },
                { name: 'Box de skins Montliers', rarity: 'Commun', id: 19 }
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '4px 8px', borderRadius: 6,
                  background: 'rgba(255,255,255,0.03)',
                  borderLeft: `3px solid ${RARITY_COLORS[item.rarity] || '#888'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, lineHeight: 1 }}>
                      {ICON_PATHS[item.id] ? (
                         <img src={ICON_PATHS[item.id]} alt={item.name} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      ) : (
                         <TitreIcon size={18} />
                      )}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: '#e0e0e0' }}>
                      {item.name}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.68rem', color: RARITY_COLORS[item.rarity], fontWeight: 600, flexShrink: 0 }}>{item.rarity}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}